/**
 * Cross-system registry of ID prefix ownership, so a prefix claimed by two
 * encoders fails at import instead of decoding to the wrong row.
 */

export type IdEncoding = "crockford32" | "base62" | "mongo-hex";

/** Which factory minted the prefix; two sources on one prefix is a collision. */
export type IdSource = "crockford32-id" | "effect-api" | "mongo";

export interface RegisteredPrefix {
  readonly prefix: string;
  readonly encoding: IdEncoding;
  readonly source: IdSource;
}

/**
 * Prefixes deliberately shared by two sources because they name the same table.
 * Both sides must encode identically, which the cross-system spec asserts.
 */
export const INTENTIONALLY_SHARED_PREFIXES: ReadonlySet<string> = new Set([
  "prd",
]);

const registry = new Map<string, RegisteredPrefix>();

/** Trailing underscore dropped so "prd_" and "prd" compare equal. */
function normalize(prefix: string): string {
  return prefix.replace(/_+$/, "");
}

/** Anything else would not survive a round trip through the ID pattern. */
const VALID_PREFIX = /^[a-z0-9_]+$/;

export function registerIdPrefix(
  prefix: string,
  encoding: IdEncoding,
  source: IdSource,
): void {
  const key = normalize(prefix);
  if (!key) {
    return;
  }
  if (!VALID_PREFIX.test(key)) {
    throw new Error(
      `ID prefix "${key}" must be lowercase letters, digits, or underscores.`,
    );
  }
  const existing = registry.get(key);
  if (existing === undefined) {
    registry.set(key, { prefix: key, encoding, source });
    return;
  }
  if (existing.source === source && existing.encoding === encoding) {
    return;
  }
  if (
    INTENTIONALLY_SHARED_PREFIXES.has(key) &&
    existing.encoding === encoding
  ) {
    return;
  }
  throw new Error(
    `ID prefix "${key}" is already registered by "${existing.source}" as ` +
      `"${existing.encoding}"; "${source}" cannot claim it as "${encoding}". ` +
      "Two entities behind one prefix make an ID string ambiguous, and two " +
      "alphabets make it decode to a different row. Pick an unused prefix.",
  );
}

export function registeredIdPrefixes(): readonly RegisteredPrefix[] {
  return [...registry.values()];
}

/** Test seam: drops all registrations so a spec can start from a clean slate. */
export function resetIdPrefixRegistryForTest(): void {
  registry.clear();
}
